'use client'

import { useNewsletterSubscription } from '@/app/hooks/mutations/useNewsletterSubscription'
import { Icons } from '@/app/components/ui/Icons'
import LoadingSpinner from '@/app/components/common/LoadingSpinner'
import Button from '@/app/components/common/Button'
import Input from '@/app/components/Input'
import Title from '@/app/components/common/Title'
import Text from '@/app/components/common/Text'
import { validateEmail } from '@/app/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { FC, FormEvent, useState } from 'react'

interface Props {
	lang: string
}

const BlogNewsletterSignup: FC<Props> = ({ lang }) => {
	const t = useTranslations('BlogNewsletter')
	const [email, setEmail] = useState('')
	const [validationError, setValidationError] = useState('')
	const [submittedEmail, setSubmittedEmail] = useState('')

	const mutation = useNewsletterSubscription()

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()

		if (!email || email.trim() === '') {
			setValidationError(t('errorEmptyEmail'))
			return
		}

		if (!validateEmail(email)) {
			setValidationError(t('errorInvalidEmail'))
			return
		}

		setValidationError('')

		mutation.mutate(
			{ email, lang },
			{
				onSuccess: () => {
					setSubmittedEmail(email)
					setEmail('')
				},
			}
		)
	}


	const handleEmailChange = (value: string) => {
		// Input component trims values, but for email we want to preserve spaces during typing
		// So we'll use the value as-is (it's already trimmed by Input)
		setEmail(value)
		if (validationError) {
			setValidationError('')
		}
		if (mutation.isSuccess) {
			mutation.reset()
		}
	}

	const displayError = validationError || (mutation.error ? t('errorInvalidEmail') : '')

	return (
		<div className="blog-newsletter-wrapper">
			<div className="blog-newsletter">
				<div className="blog-newsletter__container">
					<div className="blog-newsletter__image-wrapper">
						<Image
							src="/images/sign-up-hero-image.webp"
							alt="Two people relaxing by a pool overlooking a scenic view"
							fill
							className="blog-newsletter__image"
							sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 600px"
						/>
					</div>

					<div className="blog-newsletter__content">
						<Title level={2} bold color="black" className="blog-newsletter__title" testId="blog-newsletter-title">
							{t('title')}
						</Title>
						<Text tag="p" color="text-800" className="blog-newsletter__description">
							{t('description')}
						</Text>

						<div
							className={`blog-newsletter__success ${mutation.isSuccess ? '' : 'invisible'}`}
							role="alert"
							aria-hidden={!mutation.isSuccess}
						>
							<p className="blog-newsletter__success-message">
								{t('successMessage')}
							</p>
						</div>

						<form className="blog-newsletter__form" onSubmit={handleSubmit}>
							<div className="blog-newsletter__input-wrapper">
								<div className={`blog-newsletter__input-container ${displayError ? 'blog-newsletter__input-container--error' : ''} ${mutation.isSuccess ? 'blog-newsletter__input-container--success' : ''}`}>
									<Input
										id="blog-newsletter-email-input"
										className="blog-newsletter__input sentry-mask"
										placeholder={mutation.isSuccess ? submittedEmail : t('emailPlaceholder')}
										value={email}
										onChange={handleEmailChange}
										readonly={mutation.isPending || mutation.isSuccess}
									>
										<Icons.letter
											className="blog-newsletter__input-icon"
											stroke={displayError ? '#D61F69' : '#000000'}
										/>
									</Input>
								</div>
								{displayError && (
									<div
										id="newsletter-error"
										className="blog-newsletter__error-message"
										role="alert"
									>
										{displayError}
									</div>
								)}
							</div>

							<Button
								type="submit"
								name={t('signUpButton')}
								className="blog-newsletter__button"
								disabled={mutation.isPending || mutation.isSuccess}
							>
								{mutation.isPending ? (
									<>
										<LoadingSpinner className="blog-newsletter__spinner" />
										{t('signUpButton')}
									</>
								) : (
									t('signUpButton')
								)}
							</Button>
						</form>
					</div>
				</div>
			</div>
		</div>
	)
}

export default BlogNewsletterSignup

